import React, { useEffect, useState } from "react";
import SlideOver from "../../Modal/SlideOver";
import Button from "../../Button";
import { useTranslation } from "react-i18next";
import { Fieldset } from "@headlessui/react";
import FormError from "../../Form/FormError";
import {
  DEDUCTION_TYPE_OPTIONS,
  PAYMENT_FREQUENCY_OPTIONS,
  isBusinessWriteBlocked,
} from "../../../../Constants/business";
import CollapsibleFormItems from "../../CollapsibleForm";
import {
  addLocalNotification,
  clearBusinessEmployee,
  requestUpdateEmployeeDeductions,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import WriteBlockGate from "../../Business/WriteBlockGate";
import { cancelAPIRequest } from "../../../../Utils";
import {
  filterBenefitsByMonth,
  getEmptyFormErrors,
  getMonthName,
  isEmptyBenefitItem,
  prepareBenefitsData,
  validateBenefitItem,
} from "../../../../Utils/employeeBenefits";

const MAX_DEDUCTIONS = 10;

const fieldConfig = {
  errorKey: "deductionItems",
  itemName: "deduction",
  nameLabel: "employees.deductions.fields.name",
  namePlaceholder: "employees.deductions.placeholders.name",
  descriptionLabel: "employees.deductions.fields.description",
  descriptionPlaceholder: "employees.deductions.placeholders.description",
  typeLabel: "employees.deductions.fields.type",
  typePlaceholder: "employees.deductions.placeholders.type",
  amountLabel: "employees.deductions.fields.amount",
  amountPlaceholder: "employees.deductions.placeholders.amount",
  frequencyLabel: "employees.deductions.fields.frequency",
  frequencyPlaceholder: "employees.deductions.placeholders.frequency",
  startDateLabel: "employees.deductions.fields.startDate",
  startDatePlaceholder: "employees.deductions.placeholders.startDate",
  endDateLabel: "employees.deductions.fields.endDate",
  endDatePlaceholder: "employees.deductions.placeholders.endDate",
  reduceIncomeLabel: "employees.deductions.fields.reduceIncome",
};

const DeductionModal = ({
  open,
  onClose,
  employee,
  selectedMonth,
  selectedYear,
}) => {
  // Benefits are filtered against the payroll period being run, not today.
  const period = selectedMonth
    ? { year: selectedYear || new Date().getFullYear(), month: selectedMonth }
    : null;

  const { t } = useTranslation();
  const dispatch = useBusinessTransactionDispatch();

  const { request, deductions, deductionsLoading, deductionsError } =
    useBusinessTransactionState();
  const notificationDispatch = useNotificationDispatch();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  const [deductionItems, setDeductionItems] = useState([]);
  const [errors, setErrors] = useState({});

  const isEdit = Boolean(
    employee?.id && employee?.deductions?.items?.length > 0
  );

  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearBusinessEmployee(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isEdit) {
      const mappedDeductions = employee.deductions.items.map((deduction) => ({
        name: deduction.name || "",
        description: deduction.description || "",
        amount: deduction.amount || 0,
        type:
          DEDUCTION_TYPE_OPTIONS.find((opt) => opt.value === deduction.type) ||
          null,
        frequency:
          PAYMENT_FREQUENCY_OPTIONS.find(
            (opt) => opt.value === deduction.frequency
          ) || null,
        reduceIncome: deduction.reduceIncome || false,
        startDate: deduction.startDate ? new Date(deduction.startDate) : null,
        endDate: deduction.endDate ? new Date(deduction.endDate) : null,
      }));

      // Apply month filtering if selectedMonth is provided
      const filteredDeductions = selectedMonth
        ? filterBenefitsByMonth(mappedDeductions, period)
        : mappedDeductions;

      setDeductionItems(filteredDeductions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, isEdit, selectedMonth]);

  useEffect(() => {
    if (deductions?.employeeID) {
      // Close modal
      onClose();

      // Reset form data
      setDeductionItems([]);

      // Clear business employee update
      clearBusinessEmployee(dispatch);

      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `update-deduction-${deductions?.employeeID}`,
        type: "success",
        title: "common.success",
        body: isEdit
          ? "employees.notifications.deductionUpdated"
          : "employees.notifications.deductionCreated",
        showFull: true,
      });
    }
  }, [deductions?.employeeID, dispatch, onClose, notificationDispatch, isEdit]);

  const monthName = getMonthName(period);
  const isEmptyItem = isEmptyBenefitItem;
  const validateItem = (item) => validateBenefitItem(item, "deduction");
  const emptyErrors = getEmptyFormErrors("deduction");

  const validateForm = () => {
    const allItemsEmpty = deductionItems.every(isEmptyItem);

    if (isEdit && allItemsEmpty) return true;

    const errors = {};

    if (!isEdit && allItemsEmpty) {
      Object.assign(errors, emptyErrors);
    } else {
      const itemErrors = deductionItems.map(validateItem);
      if (itemErrors.some((error) => error !== null)) {
        errors.deductionItems = itemErrors;
      }
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isEdit && deductionItems.length === 0) return;

    if (!validateForm()) return;

    const edited_deductions = selectedMonth
      ? prepareBenefitsData(
          deductionItems,
          "deduction",
          period,
          employee?.deductions?.items
        )
      : prepareBenefitsData(deductionItems, "deduction");

    requestUpdateEmployeeDeductions(dispatch, {
      employee_id: employee.id,
      deductions: edited_deductions,
    });
  };

  const handleClose = () => {
    onClose();
    setErrors({});
    setDeductionItems([]);
    clearBusinessEmployee(dispatch);
  };

  return (
    <SlideOver
      open={open}
      onClose={handleClose}
      title={
        selectedMonth
          ? isEdit
            ? `${t("employees.modal.title.editDeduction")} - ${monthName}`
            : `${t("employees.modal.title.deduction")} - ${monthName}`
          : isEdit
          ? "employees.modal.title.editDeduction"
          : "employees.modal.title.deduction"
      }
      footer={
        <div className="flex justify-between items-center">
          <Button type="button" onClick={onClose} variant="secondary">
            {t("common.back")}
          </Button>
          <Button
            loading={deductionsLoading}
            disabled={writeBlocked || deductionsLoading}
            type="submit"
            form="deduction-form"
            variant="primary"
          >
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <form
        id="deduction-form"
        className="space-y-4 mb-6"
        onSubmit={handleSubmit}
      >
        <WriteBlockGate>
        <FormError error={deductionsError} />
        <Fieldset
          disabled={deductionsLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          <CollapsibleFormItems
            items={deductionItems}
            setItems={setDeductionItems}
            typeOptions={DEDUCTION_TYPE_OPTIONS}
            errors={errors}
            setErrors={setErrors}
            maxItems={MAX_DEDUCTIONS}
            fieldConfig={fieldConfig}
            defaultStartDate={employee?.startDate}
            titleKey="employees.deductions.label"
            addButtonKey="employees.deductions.footer.addDeduction"
            maxReachedKey="employees.deductions.footer.maxDeductionsReached"
            maxMessageKey="employees.deductions.footer.maxDeductionsMessage"
            selectedMonth={selectedMonth}
            showReduceIncome
            lockStatutoryAmounts
            employee={employee}
            initiallyCollapsed={true}
          />
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default DeductionModal;
